import React, { useEffect, useState } from 'react';
import { Plus, Save } from 'lucide-react';
import Modal from './Modal';
import { useLanguage } from '../context/LanguageContext';

const emptyForm = {
  name: '',
  position: '',
  phoneNumber: '',
  monthlySalary: '',
  joinDate: new Date().toISOString().split('T')[0],
  endDate: '',
  notes: '',
  isActive: true
};

function toDateInput(value) {
  return value ? new Date(value).toISOString().split('T')[0] : '';
}

function AddStaffModal({ isOpen, onClose, onSubmit, staff = null }) {
  const { t } = useLanguage();
  const isEdit = Boolean(staff);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  // Reload whenever the modal opens or the staff member being edited changes,
  // otherwise reopening it for a different person shows the previous values
  useEffect(() => {
    if (!isOpen) return;

    setFormData(
      staff
        ? {
            name: staff.name || '',
            position: staff.position || '',
            phoneNumber: staff.phoneNumber || '',
            monthlySalary: staff.monthlySalary ?? '',
            joinDate: toDateInput(staff.joinDate),
            endDate: toDateInput(staff.endDate),
            notes: staff.notes || '',
            isActive: staff.isActive !== false
          }
        : emptyForm
    );
  }, [isOpen, staff]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        monthlySalary: parseFloat(formData.monthlySalary) || 0,
        endDate: formData.endDate || null
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Staff Member' : 'Add Staff Member'}
      size="lg"
    >
      <div className="p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('e.g., Ram Bahadur')}
                className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('Position')}</label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                placeholder={t('e.g., Cook, Waiter')}
                className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('Phone Number')}</label>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                placeholder={t('98XXXXXXXX')}
                className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Monthly Salary (Rs.)')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-base">{t('Rs')}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  value={formData.monthlySalary}
                  onChange={(e) => setFormData({ ...formData, monthlySalary: e.target.value })}
                  placeholder="0"
                  min="0"
                  step="1"
                  className="w-full pl-8 pr-3 sm:pr-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('Salary builds up day by day from the join date')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Join Date')} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.joinDate}
                onChange={(e) => setFormData({ ...formData, joinDate: e.target.value })}
                className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {isEdit && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('Last Working Day')}</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('Leave empty if still working. Salary stops on this date.')}</p>
                </div>

                <div className="flex items-end pb-1">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">{t('Currently working')}</span>
                  </label>
                </div>
              </>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('Notes')}</label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('Anything worth remembering about this staff member')}
                className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 sm:flex-none sm:min-w-[160px] px-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 font-medium transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg disabled:opacity-60"
            >
              {isEdit ? <Save className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              <span>{isEdit ? 'Save Changes' : 'Add Staff'}</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none sm:min-w-[100px] px-6 py-3.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium transition-all duration-200 border border-gray-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default AddStaffModal;
